import { checkoutRules, useCheckoutEnv } from '../core/checkout_env.js';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { useGenUiConfig } from '../../../shared/ui/config.js';
import { useGenUiTelemetry } from '../../../shared/ui/telemetry.js';
import { BLOCK_SHELL } from '../../../shared/ui/block_shell.js';
import { CheckoutDrawer } from './checkout/checkout_drawer.js';
import { handoffPrefill } from '../core/one_click.js';
import {
  CheckoutEnvProvider,
  checkoutOfferModel,
  type CheckoutOfferModel,
} from '@voll-intelligence/intelligence-ui-kit/checkout';
import {
  useOneClickCheckout,
  type CheckoutOutcome,
  type UseOneClickCheckout,
} from '../core/use_one_click_checkout.js';
import { checkoutRoster } from './checkout/checkout_roster.js';
import { OneClickActions } from './one_click/one_click_actions.js';
import { OneClickPanel } from './one_click/one_click_panel.js';
import { ProductCheckoutCardContent } from '../products/checkout_card_content.js';
import { TravelDocsSection } from './checkout/travel_docs_section.js';
import type { CheckoutCardSlots } from './card_slots.js';
import type {
  CheckoutBookingDraft,
  CheckoutItem,
  CheckoutPrefill,
  ChatUiProductCheckout,
} from '@voll-intelligence/genui-contract';
import type { GenUiAction } from '../../../shared/ui/action.js';
import '@voll-intelligence/intelligence-ui-kit/flight-offers.css';
import '../../../shared/ui/flight_ui.css';

export interface CheckoutCardProps {
  checkout: ChatUiProductCheckout;
  note?: string;
  readOnly?: boolean;
  /** Remontado do histórico (`ChatUiView.replay`): o registro do desfecho
   *  aparece pronto, sem a animação da chegada. */
  replay?: boolean;
  /** Turno da RESERVA em voo para este card (#2116): o card mostra
   *  «Solicitando» e as ações somem até a resposta chegar. */
  busy?: boolean;
  /** Desfecho do pedido, dito pelo host quando o card de serviços do agente
   *  chega (ou falha). Sem ele, o turno que termina com o card ainda vivo conta
   *  como falha (`useOneClickCheckout`). */
  outcome?: CheckoutOutcome;
  onSubmit?: (action: Extract<GenUiAction, { type: 'product-checkout-submit' }>) => void;
  /** Leva o serviço até a viagem quando o pedido entra (`OneClickPort.arrive`). */
  onArrive?: (from: DOMRect) => Promise<void>;
  onChooseAnother?: () => void;
}

/** O card de checkout da conversa É o checkout de um clique (#5279). Este é o
 *  fluxo, igual para todo produto: o estado do 1-Click, o painel de conferência
 *  com o que o perfil e o prefill já trazem e o fecho com «Pedir com 1-Click»;
 *  o corpo, os extras e o rodapé são do produto (`ProductCheckoutCardContent`). A tela cheia (o drawer, estado interno
 *  via `createPortal`) fica atrás de «Revisar dados» — e é o único
 *  caminho quando o bloco não tem roster ou não há host que receba o envio. */
export function CheckoutCard(props: Readonly<CheckoutCardProps>) {
  const { checkout } = props;
  // Sem itens não há oferta para mostrar: nada de card, nem de 1-Click.
  const offer = checkoutOfferModel({
    items: checkout.items,
    travelerCount: checkout.travelers?.length ?? 0,
    offerFields: checkoutRules.offerFields,
  });
  const env = useCheckoutEnv();
  // The kit's checkout pieces the card reuses (the traveler fields, the ride
  // notes) read the environment from the provider; the drawer brings its own.
  return offer ? (
    <CheckoutEnvProvider env={env}>
      <OfferCheckoutCard {...props} offer={offer} />
    </CheckoutEnvProvider>
  ) : null;
}

function OfferCheckoutCard({
  checkout,
  offer,
  note,
  readOnly = false,
  replay = false,
  busy = false,
  outcome,
  onSubmit,
  onArrive,
  onChooseAnother,
}: Readonly<CheckoutCardProps & { offer: CheckoutOfferModel<CheckoutItem> }>) {
  const { t } = useGenUiI18n();
  const { renderMarkdown } = useGenUiConfig();
  const telemetry = useGenUiTelemetry();
  const [open, setOpen] = useState(false);
  // Rascunho devolvido pelo fechar (✕) do drawer FORA do 1click (CC-4
  // conversacional): o drawer desmonta por completo quando `open` vira false,
  // então esta é a ÚNICA persistência entre um fechar e a reabertura. No
  // 1click o rascunho volta para o próprio form do card.
  const [draft, setDraft] = useState<CheckoutBookingDraft | undefined>(undefined);
  // Os documentos marcados como «Tenho»: aviso, nunca trava o pedido.
  const [docsChecked, setDocsChecked] = useState<string[]>([]);
  const toggleDoc = (id: string) => {
    setDocsChecked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const flow = useOneClickCheckout({
    checkout,
    offer,
    readOnly,
    busy,
    outcome,
    onSubmit,
    travelDocsChecked: docsChecked,
  });
  const first = offer.item;
  const oneClick = flow.stage.kind !== 'off';
  const openDrawer = () => {
    telemetry.track('checkout_opened', { product: first.productType });
    setOpen(true);
  };
  const closeDrawer = (d: CheckoutBookingDraft | undefined) => {
    if (d && oneClick) applyDraft(flow, d);
    else if (d) setDraft(d);
    setOpen(false);
  };
  const actions: CheckoutCardSlots['actions'] = (ctaKey, motive, records) => (
    <OneClickActions
      stage={flow.stage}
      readOnly={readOnly}
      replay={replay}
      outcome={outcome}
      canDispatch={Boolean(onSubmit)}
      canSubmit={flow.form.canSubmit}
      blockers={flow.form.submitBlockers}
      reserveKey={ctaKey}
      {...(motive ? { motive } : {})}
      {...(records ? { records } : {})}
      onChooseAnother={onChooseAnother}
      onReview={openDrawer}
      onSubmit={flow.submit}
      onArrive={onArrive}
      onArrived={flow.markArrived}
    />
  );

  return (
    <section className="fui" aria-label={t('booking.summaryAria')}>
      {note && (
        <div className="checkout__lead">
          {renderMarkdown ? renderMarkdown(note) : <p>{note}</p>}
        </div>
      )}

      {/* A casca dos blocos da conversa (#6324): a mesma do planner, das opções e do
          cancelamento da corrida — raio 16 do 1-Click no Figma. O container `checkout`
          é a régua de largura dos produtos e do fecho. */}
      <div className={`${BLOCK_SHELL} @container/checkout`}>
        <ProductCheckoutCardContent
          slots={{
            checkout,
            flow,
            review: (
              <OneClickPanel
                flow={flow}
                prefill={checkout.prefill}
                requesterId={checkout.requesterId}
              />
            ),
            travelDocs: checkout.travelDocs ? (
              <TravelDocsSection
                travelDocs={checkout.travelDocs}
                checked={docsChecked}
                onToggle={toggleDoc}
                readOnly={readOnly || SENT_STAGES.has(flow.stage.kind)}
              />
            ) : null,
            actions,
          }}
        />
      </div>
      {open &&
        createPortal(
          /* O portal monta fora da raiz `.genui2` do host, e as custom properties do
           * DS só herdam por ancestral — daí a classe no wrapper. */
          <div className="genui2">
            <CheckoutDrawer
              open
              // Sem `onSubmit` não há para onde submeter: o drawer fica só-leitura
              // mesmo com `readOnly` falso (o pai pode soltar o callback sem
              // congelar o card). Antes, o submit virava no-op e o drawer fechava
              // marcado como despachado — a submissão do cliente evaporava.
              readOnly={readOnly || !onSubmit}
              challengeId={checkout.challengeId}
              subject={{ mode: 'offer', item: first, items: checkout.items }}
              {...checkoutRoster(checkout)}
              policyTravelerId={checkout.policyTravelerId}
              // No 1click a tela cheia abre com o que o card JÁ tem (as escolhas
              // da pessoa, não só as do perfil); fora dele, o prefill do fio.
              prefill={drawerPrefill(flow, checkout.prefill)}
              tripId={checkout.tripId}
              travelDocsChecked={docsChecked}
              {...(oneClick ? {} : { initialDraft: draft })}
              onClose={closeDrawer}
              onSubmit={({ action }) => {
                if (!onSubmit) return;
                onSubmit(action);
                flow.markSent();
                setOpen(false);
              }}
            />
          </div>,
          document.body,
        )}
    </section>
  );
}

/** O pedido já saiu (ou foi recusado): a marcação não muda mais o envio. */
const SENT_STAGES: ReadonlySet<UseOneClickCheckout['stage']['kind']> = new Set([
  'submitting',
  'done',
  'indeterminate',
  'pending_approval',
  'failed',
]);

/** O ✕ do drawer devolve motivo/justificativa/observação: no 1click eles voltam
 *  para o form do card, que é quem envia. */
function applyDraft(flow: UseOneClickCheckout, draft: CheckoutBookingDraft): void {
  flow.form.setMotiveId(draft.motiveId ?? '');
  flow.form.setJustificationId(draft.justificationId);
  flow.form.setObservation(draft.observation ?? '');
}

function drawerPrefill(
  flow: UseOneClickCheckout,
  wire: CheckoutPrefill | undefined,
): CheckoutPrefill | undefined {
  return flow.stage.kind === 'ready' ? handoffPrefill(flow.form) : wire;
}
